import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { formatPhoneForDisplay, } from '@super/shared-types';
import { actionTypeIcon, mentionIcon, ReferenceEditShell, ReferencePanel, SpineList, } from './shared';
/**
 * The action node inspector. All chrome (header, picker, summary card,
 * deciding overlay, edit shell, sticky save CTA) lives in ReferencePanel;
 * this file is only the type-specific slots.
 */
export function ActionNodePanel({ actionType, nodeActionGroupId, reference, onNavigateToAdminAction, onReplaceGroupId, renderActionPicker, renderInspector, renderInspectorFooter, onFetchUsageCount, onDuplicateGroup, onRefresh, describeMember, }) {
    return (_jsx(ReferencePanel, { headerLabel: `${actionType.toLowerCase().replace(/_/g, ' ')} action`, decidingNoun: "action", decidingNounPlural: "actions", missingLabel: "action", referenceId: nodeActionGroupId, reference: reference, renderPicker: ({ onPickReference, onStartDraft }) => renderActionPicker ? (renderActionPicker({
            onPickGroup: onPickReference,
            onStartDraft,
        })) : (_jsx("p", { className: "text-xs text-muted-foreground", children: "This action node has no group attached and no picker is wired." })), getSummaryTitle: group => {
            const name = group.name ?? 'Untitled action';
            const count = group.members.length;
            const label = count === 1 ? 'action' : 'actions';
            return `${name} • ${count} ${label}`;
        }, renderSummaryBody: group => (_jsxs(_Fragment, { children: [group.description && (_jsx("p", { className: "text-muted-foreground truncate", children: group.description })), group.members.length === 0 ? (_jsx("p", { className: "text-muted-foreground", children: "No actions yet" })) : (_jsx(SpineList, { items: buildMemberSpineItems(group, describeMember) }))] })), renderEditBody: ctx => (_jsx(ReferenceEditShell, { onBack: ctx.onDone, bodyPadding: "p-4", footer: renderInspectorFooter?.(), children: renderInspector ? (renderInspector({
                nodeActionGroupId: ctx.referenceId,
                group: ctx.reference,
                isDraft: ctx.isDraft,
                onNavigateToAdminAction,
                replaceGroupId: newId => onReplaceGroupId?.(newId),
                onDone: ctx.onDone,
            })) : (_jsx("p", { className: "text-xs text-muted-foreground", children: "No inspector wired for this action node." })) })), onReplaceReference: newId => onReplaceGroupId?.(newId), onNavigateToCatalog: onNavigateToAdminAction
            ? () => onNavigateToAdminAction(nodeActionGroupId)
            : undefined, onRefresh: onRefresh, onFetchUsageCount: onFetchUsageCount, onDuplicateImmediate: onDuplicateGroup }));
}
function memberToSpineItem(member, describe) {
    const resolved = describe?.(member) ?? null;
    const label = resolved?.label ?? describeMemberDefault(member.workflowAction);
    const Icon = resolved?.mention
        ? mentionIcon
        : actionTypeIcon(member.workflowAction.actionType);
    return {
        item: {
            key: member.workflowActionId,
            Icon,
            label,
        },
        dedupKey: resolved?.key ?? null,
    };
}
/** Build the spine's items for a group. Consecutive members that resolve
 *  to the same mention (a @role expansion fans one authored chip into N
 *  catalog rows) collapse to one line. */
export function buildMemberSpineItems(group, describe) {
    const items = [];
    let lastDedupKey = null;
    for (const member of group.members) {
        const { item, dedupKey } = memberToSpineItem(member, describe);
        if (dedupKey && dedupKey === lastDedupKey)
            continue;
        lastDedupKey = dedupKey;
        items.push(item);
    }
    return items;
}
/** A group member as an arrow names it: its own name, else what it does. */
export function memberLabel(member, describe) {
    const named = member.workflowAction.name?.trim();
    if (named)
        return named;
    const described = describe?.(member);
    if (described)
        return `${verbOf(member.workflowAction.actionType)} ${described.label}`;
    return describeMemberDefault(member.workflowAction);
}
function verbOf(actionType) {
    switch (actionType) {
        case 'EMAIL':
            return 'Email';
        case 'SMS':
            return 'SMS';
        case 'PHONE_CALL':
            return 'Call';
        default:
            return actionType.replace(/_/g, ' ').toLowerCase();
    }
}
function describeMemberDefault(action) {
    const params = (action.parameters ?? {});
    switch (action.actionType) {
        case 'EMAIL':
            return typeof params.email === 'string'
                ? `Email ${params.email}`
                : 'Email (no recipient)';
        case 'SMS':
            return typeof params.number === 'string'
                ? `SMS ${formatPhoneForDisplay(params.number)}`
                : 'SMS (no recipient)';
        case 'PHONE_CALL':
            return typeof params.number === 'string'
                ? `Call ${formatPhoneForDisplay(params.number)}`
                : 'Call (no recipient)';
        default:
            return action.actionType.replace(/_/g, ' ').toLowerCase();
    }
}
//# sourceMappingURL=ActionNodePanel.js.map